import React from 'react';

import { DateRangeFilter } from '@/components/filters/types';
import { Filter } from '@/hooks/useRisks';
import { omit } from '@/utils/lodash.util';

/**
 * Creates a filter handler for multi-select dropdown filters.
 *
 * This utility generates a filter update function that properly handles:
 * - Adding filters when selections exist
 * - Removing filter keys when selections are cleared
 * - Preserving other existing filters
 *
 * @param filterKey - The key in advancedFilters to update (e.g., 'severity', 'status', 'origin')
 * @param setFilters - State setter function for filters
 * @returns A handler function that accepts selected options and updates filters
 *
 * @example
 * const handleSeverityFilter = createMultiSelectFilterHandler('severity', setFilters);
 * handleSeverityFilter([{ value: 'C', label: 'Critical' }]);
 * // Updates filters.advancedFilters.severity = ['C']
 *
 * @example
 * const handleStatusFilter = createMultiSelectFilterHandler('status', setFilters);
 * handleStatusFilter([]); // Clears the status filter
 * // Removes filters.advancedFilters.status from the filters object
 */
export function createMultiSelectFilterHandler(
  filterKey: string,
  setFilters: React.Dispatch<React.SetStateAction<Filter>>
): (
  selectedOptions: Array<{ value?: string | number; label?: string }>
) => void {
  return (
    selectedOptions: Array<{ value?: string | number; label?: string }>
  ) => {
    setFilters(filters => ({
      ...filters,
      advancedFilters:
        selectedOptions.length > 0
          ? {
              ...filters.advancedFilters,
              [filterKey]: selectedOptions.map(
                option => option.value?.toString() || ''
              ),
            }
          : omit(filters.advancedFilters, filterKey),
    }));
  };
}

/**
 * Creates a filter handler for range selector filters (CVSS, EPSS).
 *
 * @param filterKey - The key in advancedFilters to update (e.g., 'cvss', 'epss')
 * @param setFilters - State setter function for filters
 * @returns A handler function that accepts range value and updates filters
 *
 * @example
 * const handleCVSSFilter = createRangeFilterHandler('cvss', setFilters);
 * handleCVSSFilter({ min: '7', max: '10' });
 * // Updates filters.advancedFilters.cvss = { min: '7', max: '10' }
 */
export function createRangeFilterHandler(
  filterKey: string,
  setFilters: React.Dispatch<React.SetStateAction<Filter>>
): (value: { min: string; max: string } | undefined) => void {
  return (value: { min: string; max: string } | undefined) => {
    setFilters(filters => ({
      ...filters,
      advancedFilters: { ...filters.advancedFilters, [filterKey]: value },
    }));
  };
}

/**
 * Creates a filter handler for date range filters (First Seen / Last Seen).
 *
 * @param setFilters - State setter function for filters
 * @returns A handler function that accepts date range value and updates filters
 *
 * @example
 * const handleDateRangeFilter = createDateRangeFilterHandler(setFilters);
 * handleDateRangeFilter({ field: 'created', startDate: '2024-01-01', endDate: '2024-12-31' });
 * // Updates filters.advancedFilters.dateRange
 */
export function createDateRangeFilterHandler(
  setFilters: React.Dispatch<React.SetStateAction<Filter>>
): (value: DateRangeFilter | undefined) => void {
  return (value: DateRangeFilter | undefined) => {
    setFilters(filters => ({
      ...filters,
      advancedFilters: value
        ? { ...filters.advancedFilters, dateRange: value }
        : omit(filters.advancedFilters, 'dateRange'),
    }));
  };
}

/**
 * Checks if any advanced filters are currently applied.
 *
 * This function evaluates the advancedFilters object to determine if:
 * - Any array filters have items
 * - Any object filters have defined values
 * - Any primitive filters are defined
 *
 * @param advancedFilters - The advancedFilters object from filters state
 * @returns True if any filters are applied, false otherwise
 *
 * @example
 * isDropdownFiltersApplied({ severity: ['0', '1'] }) // true
 * isDropdownFiltersApplied({ severity: [] }) // false
 * isDropdownFiltersApplied({ cvss: { min: '7', max: '10' } }) // true
 * isDropdownFiltersApplied({ cvss: { min: undefined, max: undefined } }) // false
 * isDropdownFiltersApplied({}) // false
 */
export function isDropdownFiltersApplied(
  advancedFilters: Record<string, unknown> | undefined
): boolean {
  if (!advancedFilters) {
    return false;
  }

  return !!Object.values(advancedFilters).find(filter => {
    if (Array.isArray(filter)) {
      return filter.length > 0;
    }
    if (typeof filter === 'object' && filter !== null) {
      return (
        Object.values(filter).find(value => value !== undefined) !== undefined
      );
    }
    return filter !== undefined;
  });
}

/**
 * Clears all advanced filters and the free-text search from the filters state.
 *
 * Leaves `sort` untouched (use createResetAllFiltersHandler to reset sort too).
 *
 * @param setFilters - State setter function for filters
 * @returns A function that clears all advanced filters + search when called
 *
 * @example
 * const clearAllFilters = createClearAllFiltersHandler(setFilters);
 * clearAllFilters(); // Removes advancedFilters and clears search
 */
export function createClearAllFiltersHandler(
  setFilters: React.Dispatch<React.SetStateAction<Filter>>
): () => void {
  return () => {
    setFilters(filters => ({
      ...omit(filters, 'advancedFilters'),
      search: '',
      negatedFilterKeys: [],
    }));
  };
}

/**
 * Clears all filters including search, sort, and advanced filters.
 *
 * @param setFilters - State setter function for filters
 * @returns A function that resets all filters to initial state when called
 *
 * @example
 * const resetAllFilters = createResetAllFiltersHandler(setFilters);
 * resetAllFilters(); // Sets filters to { search: '', sort: undefined, advancedFilters: undefined }
 */
export function createResetAllFiltersHandler(
  setFilters: React.Dispatch<React.SetStateAction<Filter>>
): () => void {
  return () => {
    setFilters({
      search: '',
      sort: undefined,
      advancedFilters: undefined,
      negatedFilterKeys: [],
    });
  };
}

/**
 * Extracts selected values from advancedFilters for a given filter key.
 *
 * @param advancedFilters - The advancedFilters object from filters state
 * @param filterKey - The key to extract values for
 * @returns Array of selected values or empty array if none exist
 *
 * @example
 * getSelectedFilterValues({ severity: ['0', '1'] }, 'severity') // ['0', '1']
 * getSelectedFilterValues({ severity: [] }, 'severity') // []
 * getSelectedFilterValues({}, 'severity') // []
 */
export function getSelectedFilterValues(
  advancedFilters: Record<string, unknown> | undefined,
  filterKey: string
): string[] {
  if (!advancedFilters || !advancedFilters[filterKey]) {
    return [];
  }

  const value = advancedFilters[filterKey];
  if (Array.isArray(value)) {
    return value as string[];
  }

  return [];
}
